import * as React from "react";
import { IListObj } from "../types/genericTypes";
import "../../assets/scss/generic/autocomplete.scss";

export interface IAutocompleteProps {
  id?: string; // id of the input feild
  name: string; // name of the input field
  className?: string; // extra classname for container if required
  data: Array<IListObj> | null; // dropdown array
  noDataMsg: string; // msg to show when data does not exist in dropdown array
  inputClassName?: string; // extra classname for input field
  errorText?: string; // red error text
  prefix?: React.ReactChild; // text/element to show before the input field
  suffix?: React.ReactChild; // text/element to show after the input field
  placeholder?: string; // placeholder
  helpText?: string;
  defaultValue?: string; // help text
  helpTextColor?: "grey" | "blue"; // the color of help text, takes gray as default
  onOptionSelect: (data: IListObj) => void; // function to implement on input select
  onChange: (value: string) => void; // function to be called on change
  onBlur?: (value: string) => void; // function to be called on blur
  required?: boolean;
}

const Autocomplete: React.FC<IAutocompleteProps> = (props) => {
  const [show, setShowDropdown] = React.useState(false);
  // sets the default value of input if present
  // value: value of the input (type = string)
  // setValue: function to set value
  const [value, setValue] = React.useState(
    ""
    // props.defaultValue ? props.defaultValue : ""
  );

  // when focussed, this helps to colour the border of input_field_cont
  const [focusClass, setFocusClass] = React.useState("");

  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);

  // ACCESSIBILITY: tracks which option is keyboard-focused so aria-activedescendant
  // can point the screen reader to the correct option in the listbox
  const [activeIndex, setActiveIndex] = React.useState(-1);

  // ACCESSIBILITY: text for the live region — announces result count and selection
  // to screen readers without moving focus away from the input
  const [announcement, setAnnouncement] = React.useState("");

  // ACCESSIBILITY: ref for the listbox ul so we can scroll options into view
  // during keyboard navigation without the user needing to use the mouse
  const listRef = React.useRef<HTMLUListElement>(null);

  const describedBy =
  props.errorText
    ? `${props.name}-error`
    : props.helpText
    ? `${props.name}-help`
    : undefined;

  // ACCESSIBILITY: stable id for the active option, passed to aria-activedescendant
  // undefined when no option is highlighted so the attribute is omitted entirely
  const activeOptionId =
    activeIndex >= 0 ? `${props.name}-option-${activeIndex}` : undefined;

  function showDropdown(value: string) {
    value ? setShowDropdown(true) : setShowDropdown(false);
  }

  function handleBlur() {
    hideDropDown();
    props.onBlur && props.onBlur(value);
    setFocusClass("");
    // ACCESSIBILITY: reset active index on blur so stale aria-activedescendant
    // is not left pointing at an option after the dropdown closes
    setActiveIndex(-1);
  }

  function hideDropDown() {
    setTimeout(() => {
      setShowDropdown(false);
      // ACCESSIBILITY: clear active index when dropdown hides via timeout
      // (e.g. clicking outside) so the combobox state stays consistent
      setActiveIndex(-1);
    }, 300);
  }

  function handleInputChange(value: string) {
    showDropdown(value);
    setValue(value);
    // ACCESSIBILITY: reset active index on every keystroke so the highlight
    // always starts fresh when the result list changes
    setActiveIndex(-1);

    // ACCESSIBILITY: announce how many suggestions are available so screen reader
    // users know they can arrow-key into the list without having to guess
    if (value && props.data) {
      const count = props.data.length;
      setAnnouncement(
        count > 0
          ? `${count} suggestion${count > 1 ? "s" : ""} available. Use arrow keys to navigate.`
          : props.noDataMsg
      );
    } else {
      setAnnouncement("");
    }

    return props.onChange(value);
  }

  function onOptionSelect(d: IListObj) {
    setShowDropdown(false);
    setValue(d.display);
    // ACCESSIBILITY: announce the selected value so screen readers confirm
    // which city was chosen after the dropdown closes
    setAnnouncement(`${d.display} selected.`);
    return props.onOptionSelect(d);
  }

  // ACCESSIBILITY: full keyboard navigation handler for the combobox
  // ArrowDown/Up moves highlight, Enter selects, Escape closes the dropdown
  // This is required by WCAG 2.1 SC 1.3.1 and the ARIA combobox pattern
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!show || !props.data || props.data.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault(); // prevent page scroll
      const next = Math.min(activeIndex + 1, props.data.length - 1);
      setActiveIndex(next);
      scrollOptionIntoView(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); // prevent page scroll
      const prev = Math.max(activeIndex - 1, 0);
      setActiveIndex(prev);
      scrollOptionIntoView(prev);
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault(); // prevent form submission on option select
      onOptionSelect(props.data[activeIndex]);
    } else if (e.key === "Escape") {
      // ACCESSIBILITY: Escape closes dropdown and returns focus to input
      // per the ARIA combobox keyboard interaction pattern
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  }

  // ACCESSIBILITY: scrolls the highlighted option into view inside the listbox
  // so keyboard users don't have to manually scroll to see the focused item
  function scrollOptionIntoView(index: number) {
    const list = listRef.current;
    if (!list) return;
    const item = list.querySelectorAll('[role="option"]')[index] as HTMLElement;
    item?.scrollIntoView({ block: "nearest" });
  }

  const dropdownList = props.data
    ? props.data.map((d, index) => {
        const capitalData = d.display.replace(/\w\S*/g, (w) =>
          w.replace(/^\w/, (c) => c.toUpperCase())
        );

        const dropdownData = capitalData.replace(
          new RegExp(value, "i"),
          "<b>$&</b>"
        );

        return (
          <li
            className={
              "dropdown_list" +
              // ACCESSIBILITY: apply active/highlight class when this option is
              // keyboard-focused via activeIndex so sighted keyboard users can
              // see which option is currently highlighted — without this the
              // keyboard navigation works for screen readers but is invisible
              // to sighted users who are not using a mouse
              (activeIndex === index ? " keyboard_active" : "")
            }
            key={d.value}
            // ACCESSIBILITY: unique id per option so aria-activedescendant on
            // the input can point to the currently keyboard-focused option
            id={`${props.name}-option-${index}`}
            role="option"
            // ACCESSIBILITY: aria-selected dynamically reflects keyboard highlight
            // instead of being hardcoded false — screen readers read this state
            aria-selected={activeIndex === index}
            onClick={() => onOptionSelect(d)}
          >
            <div className="dropdown_data">
              <span
                dangerouslySetInnerHTML={{
                  __html: dropdownData,
                }}
              ></span>
            </div>
          </li>
        );
      })
    : null;

  React.useEffect(() => {
    if (props.defaultValue === "") {
      setValue("");
    }
  }, [props.defaultValue]);

  return (
    <div className={"autocomplete " + props.className}>

      {/* ACCESSIBILITY: visually hidden live region that announces result count
          and selection confirmations to screen readers via aria-live="polite".
          It is hidden from sighted users but always read by assistive technology
          when its text content changes, without interrupting the current reading flow */}
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
        }}
      >
        {announcement}
      </span>

      <div className={"input_field_wrapper " + props.inputClassName}>
        <div
          className={
            "input_field_cont" + (props.errorText ? " error" : focusClass)
          }
        >
          {props.prefix && <div className="input_prefix">{props.prefix}</div>}
          <input
            name={props.name}
            id={props.id || props.name}
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            // ACCESSIBILITY: keyboard navigation handler added — allows
            // arrow keys, Enter, and Escape to control the dropdown
            onKeyDown={handleKeyDown}
            placeholder={props.placeholder}
            className="input_field"
            onFocus={() => setFocusClass(" focus")}
            onBlur={handleBlur}
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={show}
            aria-controls={`${props.name}-dropdown`}
            // ACCESSIBILITY: points to the id of the currently highlighted option
            // so screen readers announce it as the user arrows through the list
            aria-activedescendant={activeOptionId}
            aria-required={props.required || undefined}
            aria-describedby={describedBy}
            aria-invalid={!!props.errorText}
          />
          {props.suffix && <div className="input_suffix">{props.suffix}</div>}
        </div>
        {props.errorText && (
          // ACCESSIBILITY: role="alert" makes errors announced immediately
          // by screen readers without the user needing to move focus
          <p id={`${props.name}-error`} className="help_text red" role="alert">
            {props.errorText}
          </p>
        )}
        {props.helpText && (
          <p id={`${props.name}-help`} className={`help_text ${props.helpTextColor || "grey"}`}>
            {props.helpText}
          </p>
        )}
      </div>

      <ul
        // ACCESSIBILITY: ref added so scrollOptionIntoView can find and scroll
        // the highlighted option without moving browser focus out of the input
        ref={listRef}
        className={"dropdown" + (show ? " active" : "")}
        id={`${props.name}-dropdown`}
        role="listbox"
        // ACCESSIBILITY: aria-label gives the listbox a name so screen readers
        // announce context (e.g. "City suggestions") when the list is opened
        aria-label={`${props.placeholder || props.name} suggestions`}
      >
        {dropdownList !== null &&
          (dropdownList.length ? (
            dropdownList
          ) : (
            // ACCESSIBILITY: role="status" on the no-data message ensures it is
            // announced politely when no results are found
            <li className="no-data-msg" role="status">{props.noDataMsg}</li>
          ))}
      </ul>
    </div>
  );
};

export default Autocomplete;